import React, { createContext, useContext, useState, useEffect } from "react"
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth"
import { auth, USER_TYPES } from "../firebase"
import { createUserProfile, getUserProfile, updateUserProfile, checkPendingInvitesForEmail, acceptStudioInvite } from "../utils/firebaseUtils"

const AuthContext = createContext()

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

const EMAIL_FOR_SIGN_IN_KEY = "emailForSignIn"

function getInviteCodeFromURL() {
  const params = new URLSearchParams(window.location.search)
  return params.get("inviteCode") || null
}

function cleanupURL() {
  const url = new URL(window.location.href)
  url.search = ""
  url.hash = ""
  window.history.replaceState({}, document.title, url.pathname)
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  const isMagicLink = isSignInWithEmailLink(auth, window.location.href)
  const pendingInviteCode = getInviteCodeFromURL()

  function signup(email, password, displayName, userType = USER_TYPES.REGULAR) {
    return createUserWithEmailAndPassword(auth, email, password).then(async userCredential => {
      // Update the user's display name
      await updateProfile(userCredential.user, {
        displayName: displayName,
      })

      // Create user profile in Firestore
      await createUserProfile(userCredential.user.uid, {
        email: email,
        displayName: displayName,
        userType: userType,
      })

      // Auto-accept any pending email invites for this user
      await checkPendingInvitesForEmail(email, userCredential.user.uid)

      return userCredential
    })
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password)
  }

  function logout() {
    return signOut(auth)
  }

  async function resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email)
    } catch (error) {
      console.error("Error sending password reset email:", error)
      throw error
    }
  }

  async function sendInviteLink(email, inviteCode) {
    const actionCodeSettings = {
      url: `${window.location.origin}?inviteCode=${inviteCode}&email=${encodeURIComponent(email)}`,
      handleCodeInApp: true,
    }

    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings)
      localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email)
    } catch (error) {
      console.error("Error sending invite link:", error)
      throw error
    }
  }

  async function completeSignInWithLink(email, displayName) {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      throw new Error("This is not a valid sign-in link")
    }

    try {
      const result = await signInWithEmailLink(auth, email, window.location.href)
      const user = result.user

      let profile = await getUserProfile(user.uid)
      if (!profile) {
        const finalDisplayName = displayName || email.split("@")[0]
        await updateProfile(user, { displayName: finalDisplayName })
        await createUserProfile(user.uid, {
          email: email,
          displayName: finalDisplayName,
          userType: USER_TYPES.REGULAR,
        })
        profile = await getUserProfile(user.uid)
      }

      const inviteCode = getInviteCodeFromURL()
      let joinResult = null
      if (inviteCode) {
        try {
          joinResult = await acceptStudioInvite(inviteCode, user.uid)
        } catch (err) {
          console.error("Error accepting invite after sign-in:", err)
        }
      }

      localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY)
      cleanupURL()

      return joinResult
    } catch (error) {
      console.error("Error completing sign-in with link:", error)
      throw error
    }
  }

  async function updateDisplayName(newDisplayName) {
    if (!currentUser) {
      throw new Error("No user logged in")
    }

    try {
      // Update Firebase Auth profile
      await updateProfile(currentUser, {
        displayName: newDisplayName,
      })

      // Update Firestore user profile
      await updateUserProfile(currentUser.uid, {
        displayName: newDisplayName,
      })

      // Reload user profile
      const updatedProfile = await getUserProfile(currentUser.uid)
      setUserProfile(updatedProfile)

      // Force update currentUser to reflect the change
      setCurrentUser({ ...currentUser, displayName: newDisplayName })

      return true
    } catch (error) {
      console.error("Error updating display name:", error)
      throw error
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      setCurrentUser(user)

      if (user) {
        // Load user profile from Firestore
        try {
          const profile = await getUserProfile(user.uid)
          setUserProfile(profile)
        } catch (error) {
          console.error("Error loading user profile:", error)
          setUserProfile(null)
        }
      } else {
        setUserProfile(null)
      }

      setLoading(false)
    })

    return unsubscribe
  }, [])

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    logout,
    resetPassword,
    updateDisplayName,
    sendInviteLink,
    completeSignInWithLink,
    isMagicLink,
    pendingInviteCode,
  }

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>
}
